import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import { API_URL } from '@/src/services/api';
import HeroChart, {
    AxisTick,
    ChartSeries,
} from '../components/HeroChart';
import { eventDay } from '../lib/eventStats';
import '../tokens.css';

/*
 * Client report — the page behind the magic link (mock:
 * mocks/client-report.html). Unauthenticated by design: no auth
 * hooks, no cookies (bare axios), aggregate payload only. Polls
 * every 30s while the event is live (latest activity < 45 min),
 * every 5 min after. Dark follows the client's OS.
 */

/* no withCredentials — this page must not ship auth cookies */
const publicApi = axios.create({ baseURL: API_URL });

const LIVE_WINDOW_MS = 45 * 60 * 1000;
const POLL_LIVE_MS = 30 * 1000;
const POLL_QUIET_MS = 5 * 60 * 1000;

interface Bucket {
    bucket: string;
    count: number;
}

interface DayRow {
    day: string; // 'YYYY-MM-DD', event-local with a 6 AM boundary
    photos: number;
    shares: number;
}

interface Report {
    event: { name: string; date: string; location: string | null };
    org: { name: string; logo: string | null } | null;
    stats: { photos: number; shares: number };
    series: { unit: 'hour' | 'day'; photos: Bucket[]; shares: Bucket[] };
    byDay?: DayRow[] | null;
    gallery: { url: string } | null;
}

/* one metric line in the By day block: thin bar (shared scale across
   the whole block) + the count in ink — identity rides on the bar
   color matching the chart legend, never on color alone */
function BarLine({
    value,
    max,
    color,
}: {
    value: number;
    max: number;
    color: string;
}) {
    return (
        <div className="grid grid-cols-[1fr_52px] items-center gap-3">
            <span
                className="block h-[5px] min-w-[3px] rounded-r-full"
                style={{
                    width: `${((value / max) * 100).toFixed(1)}%`,
                    background: color,
                }}
            />
            <span className="text-right text-[12.5px] tabular-nums">
                {value.toLocaleString()}
            </span>
        </div>
    );
}

function Tile({ value, label }: { value: string; label: string }) {
    return (
        <div>
            <div className="text-[30px] font-bold leading-none tracking-[-0.02em] tabular-nums">
                {value}
            </div>
            <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                {label}
            </div>
        </div>
    );
}

export default function ClientReportV2() {
    const { token } = useParams();
    const [report, setReport] = useState<Report | null>(null);
    const [failed, setFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [dark, setDark] = useState(
        () =>
            window.matchMedia &&
            window.matchMedia('(prefers-color-scheme: dark)').matches,
    );
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            const data = await publicApi
                .get(`/public/report/${token}`)
                .then((r) => r.data as Report)
                .catch((e) => {
                    if (e?.response?.status === 404 && mounted)
                        setFailed(true);
                    return null;
                });
            if (!mounted) return;
            if (data) {
                setReport(data);
                setFailed(false);
            }
            setIsLoading(false);
            const latest = data
                ? Math.max(
                      0,
                      ...[...data.series.photos, ...data.series.shares].map(
                          (b) => moment(b.bucket).valueOf(),
                      ),
                  )
                : 0;
            const live = Date.now() - latest < LIVE_WINDOW_MS;
            timer.current = setTimeout(
                load,
                live ? POLL_LIVE_MS : POLL_QUIET_MS,
            );
        };
        load();
        return () => {
            mounted = false;
            if (timer.current) clearTimeout(timer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const latestActivity = useMemo(() => {
        if (!report) return 0;
        return Math.max(
            0,
            ...[...report.series.photos, ...report.series.shares].map((b) =>
                moment(b.bucket).valueOf(),
            ),
        );
    }, [report]);
    const live =
        latestActivity > 0 && Date.now() - latestActivity < LIVE_WINDOW_MS;

    /* cumulative chart from the server's count buckets — gaps filled
       with zero so quiet hours draw as honest flat segments, and the
       timeline extends to now while live */
    const chart = useMemo(() => {
        if (!report) return null;
        const { unit } = report.series;
        const all = [...report.series.photos, ...report.series.shares];
        if (all.length === 0) return null;
        const times = all.map((b) => moment(b.bucket).valueOf());
        const start = moment(Math.min(...times)).startOf(unit);
        const endActivity = moment(Math.max(...times)).startOf(unit);
        const end = live
            ? moment(Math.max(endActivity.valueOf(), moment().startOf(unit).valueOf()))
            : endActivity;

        const buckets: number[] = [];
        const indexOf = new Map<number, number>();
        const cursor = start.clone();
        while (cursor.valueOf() <= end.valueOf() && buckets.length < 400) {
            indexOf.set(cursor.valueOf(), buckets.length);
            buckets.push(cursor.valueOf());
            cursor.add(1, unit);
        }
        if (buckets.length < 2) return null;

        const cumulative = (rows: Bucket[]) => {
            const per = buckets.map(() => 0);
            rows.forEach((b) => {
                const i = indexOf.get(
                    moment(b.bucket).startOf(unit).valueOf(),
                );
                if (i != null) per[i] += b.count;
            });
            let run = 0;
            return per.map((v) => (run += v));
        };

        const series: ChartSeries[] = [
            {
                label: 'Photos',
                color: 'hsl(var(--brand))',
                values: cumulative(report.series.photos),
            },
        ];
        if (report.series.shares.length > 0) {
            series.push({
                label: 'Shares',
                color: 'hsl(var(--attention))',
                values: cumulative(report.series.shares),
            });
        }

        const axis: AxisTick[] = [0, 1, 2, 3, 4].map((k) => {
            const i = Math.round((k * (buckets.length - 1)) / 4);
            const isLast = i === buckets.length - 1;
            return {
                label:
                    isLast && live
                        ? 'now'
                        : moment(buckets[i]).format(
                              unit === 'hour' ? 'h A' : 'MMM D',
                          ),
                strong: isLast && live,
            };
        });

        return { series, axis };
    }, [report, live]);

    const shell = (children: React.ReactNode) => (
        <div
            className={
                'psv2 min-h-screen bg-background text-foreground' +
                (dark ? ' dark' : '')
            }
        >
            <div className="mx-auto max-w-[560px] px-[22px] pb-10 pt-[34px]">
                {children}
            </div>
        </div>
    );

    if (isLoading) {
        return shell(
            <>
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </>,
        );
    }

    if (failed || !report) {
        return shell(
            <div className="pt-16 text-center">
                <p className="text-[15px] font-semibold">
                    This link isn't active.
                </p>
                <p className="mt-1.5 text-[13px] text-muted-foreground">
                    Check with whoever sent it to you.
                </p>
            </div>,
        );
    }

    const { event, org, stats, gallery } = report;
    const byDay =
        report.byDay && report.byDay.length > 1 ? report.byDay : null;
    const byDayMax = byDay
        ? Math.max(...byDay.map((r) => Math.max(r.photos, r.shares)), 1)
        : 1;
    const d = event.date ? eventDay(event.date) : null;
    /* Photos + Shares only (Josh, 2026-08-22 — sessions, share
       rate, and gallery views cut from the client view) */
    const tiles: { value: string; label: string }[] = [
        { value: stats.photos.toLocaleString(), label: 'Photos' },
        { value: stats.shares.toLocaleString(), label: 'Shares' },
    ];

    return shell(
        <>
            <span className="mb-[18px] flex h-[44px] w-[44px] items-center justify-center overflow-hidden rounded-[12px] border border-border bg-sunk text-[17px] font-extrabold tracking-[-0.03em]">
                {org?.logo ? (
                    <img
                        src={org.logo}
                        alt={org?.name ?? ''}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    (org?.name ?? event.name ?? 'S')[0]?.toUpperCase()
                )}
            </span>
            <h1 className="text-[26px] font-bold leading-[1.15] tracking-[-0.02em]">
                {event.name}
            </h1>
            <p className="mt-1.5 text-[13.5px] text-muted-foreground">
                {d?.isValid() ? d.format('MMM D, YYYY') : ''}
                {event.location ? ` · ${event.location}` : ''}
            </p>

            <div className="mb-7 mt-3.5 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]">
                {live ? (
                    <>
                        <span className="ps-pulse ps-pulse-live" />
                        <span className="text-attention">
                            Live · updates every 30s
                        </span>
                    </>
                ) : (
                    <>
                        <span className="h-2 w-2 flex-none rounded-full bg-off" />
                        <span className="text-faint">
                            {d?.isValid()
                                ? `Wrapped · ${d.format('MMM D')}`
                                : 'Wrapped'}
                        </span>
                    </>
                )}
            </div>

            <div className="mb-8 grid grid-cols-2 gap-x-4 gap-y-6">
                {tiles.map((tile) => (
                    <Tile
                        key={tile.label}
                        value={tile.value}
                        label={tile.label}
                    />
                ))}
            </div>

            {chart && (
                <div className="mb-8">
                    <div className="mb-2 flex items-baseline justify-between">
                        <span className="ps-label">Photos + shares</span>
                    </div>
                    <HeroChart
                        series={chart.series}
                        axis={chart.axis}
                        bleed={false}
                    />
                </div>
            )}

            {/* multi-day breakdown (variant A, mock: client-report.html):
                one row per event day, both metrics on one shared scale
                so the big day is obvious at a glance */}
            {byDay && (
                <div className="mb-9">
                    <div className="mb-1 flex items-baseline justify-between">
                        <span className="ps-label">By day</span>
                    </div>
                    {byDay.map((row, i) => {
                        const rd = moment(row.day, 'YYYY-MM-DD');
                        return (
                            <div
                                key={row.day}
                                className={
                                    'flex items-center gap-4 py-[13px]' +
                                    (i > 0 ? ' border-t border-border' : '')
                                }
                            >
                                <div className="w-[76px] flex-none">
                                    <div className="text-[13.5px] font-semibold tracking-[-0.01em]">
                                        {rd.format('ddd')}
                                    </div>
                                    <div className="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-faint">
                                        {rd.format('MMM D')}
                                    </div>
                                </div>
                                <div className="grid min-w-0 flex-1 gap-2">
                                    <BarLine
                                        value={row.photos}
                                        max={byDayMax}
                                        color="hsl(var(--brand))"
                                    />
                                    <BarLine
                                        value={row.shares}
                                        max={byDayMax}
                                        color="hsl(var(--attention))"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {gallery?.url && (
                <a
                    href={gallery.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-9 block w-full rounded-full bg-foreground px-[18px] py-[13px] text-center text-[14px] font-semibold text-background no-underline"
                >
                    View the gallery →
                </a>
            )}

            <div className="flex items-center justify-center gap-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                <span className="flex h-[14px] w-[14px] items-center justify-center rounded-full bg-foreground text-[8px] tracking-normal text-background">
                    S
                </span>
                Powered by Smilebooth
            </div>
        </>,
    );
}
